/**
 * Advanced Retirement Graph with Monte Carlo bands
 * Displays portfolio projection over time with percentile confidence bands
 * and life event markers.
 *
 * Single source of truth: all props (deterministicProjection, retirementAge, etc.)
 * are derived from the same calculation run in the parent. The parent runs
 * runProjection + recomputePensionFromInputs together and passes result + inputs
 * here. This component is presentational only — it does not mutate inputs or
 * hold local state that would diverge from that source.
 */

import { useEffect, useRef, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { nFormatter } from "~/utils/formatNumbers";
import { BRAND_PRIMARY_RGB, BRAND_SECONDARY_RGB } from "~/brandColors";
import { useLanguage } from "~/hooks/useLanguage";
import { useCurrency } from "~/hooks/useCurrency";
import { computeNiceYTicks } from "./retirementGraph";
import { IconButton, Menu, Tooltip, Toggle } from "~/components/ui";
import {
  LifeEventDirection,
  LifeEventFrequency,
  RetirementGoalKind,
  isCashFlowEvent,
  isBuyAssetEvent,
  isSellAssetEvent,
  isRecurringEvent,
  getEffectiveEndYear,
  type ProjectionYearPoint,
  type MonteCarloResult,
  type LifeEvent,
  type BuyAssetEvent,
  type SellAssetEvent,
  type RetirementGoal,
} from "~/domain/retirement/advancedRetirement";
import {
  getLifeEventIcons,
  getEventTypeInfo,
  getCategoryInfo,
} from "./uiConstants";
import { translations, type TranslationKey } from "./translations";

export interface AdvancedRetirementGraphData {
  id: string;
  color: string;
  data: {
    x: number;
    y: number;
  }[];
}

/** All props come from the same calculation run in the parent (single source of truth). */
interface AdvancedRetirementGraphProps {
  deterministicProjection: ProjectionYearPoint[];
  monteCarlo?: MonteCarloResult;
  fireNumber: number;
  fireYear?: number | null;
  retirementAge: number;
  pensionEligibilityAge?: number;
  currentAge: number;
  lifeExpectancy: number;
  showMonteCarlo: boolean;
  lifeEvents?: LifeEvent[];
  goals?: RetirementGoal[];
  /** When true, the chart expands to fill its parent container height */
  fullscreen?: boolean;
  /** When true, hides the controls bar (zoom, toggles) */
  hideControls?: boolean;
}

// ============================================================================
// LIFE EVENT GROUPING
// ============================================================================

interface EventMarkerGroup {
  /** Display age (center of the group) */
  age: number;
  /** All events in this group */
  events: LifeEvent[];
  /** Summary label */
  label: string;
}

/**
 * Get a short label for a single life event
 */
function getEventShortLabel(event: LifeEvent): string {
  return event.name;
}

/**
 * Get the color for a life event marker
 */
function getEventColor(event: LifeEvent): string {
  if (isCashFlowEvent(event)) {
    return event.direction === LifeEventDirection.INFLOW
      ? "#4CAF50"
      : "#f44336";
  }
  if (isBuyAssetEvent(event)) return BRAND_SECONDARY_RGB;
  if (isSellAssetEvent(event)) return "#f44336";
  return "#607D8B";
}

/**
 * Get the icon key string for a life event (user-chosen icon or null)
 */
function getEventIconKey(event: LifeEvent): string | undefined {
  return event.icon;
}

// isRecurringEvent, isIncomeRelatedEvent, getEffectiveEndYear are imported
// from ~/domain/retirement/advancedRetirement (single source of truth).

// ============================================================================
// EVENT SIZE SCALING
// ============================================================================

/**
 * Compute the total monetary worth of a life event over its entire duration.
 * - ONE_TIME: just the amount
 * - MONTHLY: amount * 12 * years
 * - YEARLY: amount * years
 *
 * @param fallbackEndYear Used as the end year for recurring events without an explicit endYear
 */
function computeEventTotalWorth(
  event: LifeEvent,
  fallbackEndYear: number,
  retirementYear: number,
  pensionYear?: number
): number {
  const endYear = getEffectiveEndYear(
    event,
    fallbackEndYear,
    retirementYear,
    pensionYear
  );
  const duration = Math.max(1, endYear - event.startYear);

  switch (event.frequency) {
    case LifeEventFrequency.MONTHLY:
      return event.amount * 12 * duration;
    case LifeEventFrequency.YEARLY:
      return event.amount * duration;
    case LifeEventFrequency.ONE_TIME:
    default:
      return event.amount;
  }
}

/**
 * Compute a scale factor for each life event based on its monetary worth.
 *
 * Uses a logarithmic scale with adaptive range:
 * - Full range [0.6, 1.7] applies when there is a ~100x (2 log orders) difference
 * - Smaller differences compress the range proportionally
 * - If all events have similar worth, all icons are close to 1.0x (normal size)
 *
 * @returns Map from event ID to scale factor (0.6–1.7)
 */
function computeEventScaleFactors(
  events: LifeEvent[],
  fallbackEndYear: number,
  retirementYear: number,
  pensionYear?: number
): Map<string, number> {
  const enabledEvents = events.filter((e) => e.enabled);
  if (enabledEvents.length === 0) return new Map();

  // Compute log10 of total worth for each event (floor at 1 to avoid log(0))
  const logWorths = enabledEvents.map((e) => ({
    id: e.id,
    logWorth: Math.log10(
      Math.max(
        1,
        computeEventTotalWorth(e, fallbackEndYear, retirementYear, pensionYear)
      )
    ),
  }));

  const minLog = Math.min(...logWorths.map((w) => w.logWorth));
  const maxLog = Math.max(...logWorths.map((w) => w.logWorth));
  const logRange = maxLog - minLog;

  // Adaptive range: how much of [0.6, 1.7] to use.
  // Full range when logRange >= 2 (100x monetary difference).
  const spread = Math.min(1, logRange / 2);
  const minScale = 1.0 - 0.4 * spread; // 1.0 → 0.6
  const maxScale = 1.0 + 0.7 * spread; // 1.0 → 1.7

  const result = new Map<string, number>();
  for (const w of logWorths) {
    const t = logRange > 0 ? (w.logWorth - minLog) / logRange : 0.5;
    result.set(w.id, minScale + t * (maxScale - minScale));
  }

  return result;
}

/**
 * Group life events by proximity on the age axis.
 * Events within `minAgeGap` years of each other get merged into a single marker.
 * When `skipRecurring` is true, recurring events are excluded (they render as duration bars instead).
 */
function groupLifeEvents(
  events: LifeEvent[],
  currentAge: number,
  currentYear: number,
  minAgeGap: number,
  skipRecurring = true
): EventMarkerGroup[] {
  // Build a list of (age, event) pairs, only for enabled events with a start year
  const ageEvents: { age: number; event: LifeEvent }[] = [];
  for (const event of events) {
    if (!event.enabled) continue;
    // Optionally skip recurring events — they render as duration bars above the chart
    if (skipRecurring && isRecurringEvent(event)) continue;
    const age = currentAge + (event.startYear - currentYear);
    ageEvents.push({ age, event });
    // For recurring events with an endYear, also mark the end
    // (we only mark the start for simplicity on the graph)
  }

  // Sort by age
  ageEvents.sort((a, b) => a.age - b.age);

  // Cluster events that are within minAgeGap of each other
  const groups: EventMarkerGroup[] = [];
  for (const { age, event } of ageEvents) {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && Math.abs(age - lastGroup.age) < minAgeGap) {
      // Merge into existing group
      lastGroup.events.push(event);
      // Recalculate center age
      const allAges = lastGroup.events.map(
        (e) => currentAge + (e.startYear - currentYear)
      );
      lastGroup.age = Math.round(
        allAges.reduce((sum, a) => sum + a, 0) / allAges.length
      );
    } else {
      groups.push({
        age,
        events: [event],
        label: "",
      });
    }
  }

  // Build labels
  for (const group of groups) {
    if (group.events.length === 1) {
      group.label = getEventShortLabel(group.events[0]);
    } else {
      group.label = `${group.events.length} events`;
    }
  }

  return groups;
}

/**
 * Look up the Y value on a data series for a given age (x).
 * Uses linear interpolation between the two nearest data points.
 */
function interpolateY(
  series: { x: number; y: number }[],
  age: number
): number | null {
  if (series.length === 0) return null;
  // Clamp to series bounds
  if (age <= series[0].x) return series[0].y;
  if (age >= series[series.length - 1].x) return series[series.length - 1].y;
  // Find the two surrounding points
  for (let i = 0; i < series.length - 1; i++) {
    if (age >= series[i].x && age <= series[i + 1].x) {
      const t = (age - series[i].x) / (series[i + 1].x - series[i].x);
      return series[i].y + t * (series[i + 1].y - series[i].y);
    }
  }
  return null;
}

// ============================================================================
// RECURRING EVENT LANE PACKING
// ============================================================================

/** Compact mode: how many px of each bar peek out when stacked behind the front bar */
const RECURRING_STACK_PEEK = 8;
/** Expanded mode: vertical gap between bars within the same type band */
const RECURRING_INTRA_GAP = 2;

interface RecurringBarInfo {
  event: LifeEvent;
  startAge: number;
  endAge: number;
  color: string;
  /** Sub-index within the type group (0 = oldest) */
  stackIndex: number;
  /** How many bars are in this type group */
  stackSize: number;
  /** Type key for grouping */
  typeKey: string;
  /** Pre-computed Y position (negative, above chart) */
  barY: number;
}

/**
 * Get a grouping key for a recurring event.
 * Events with the same key are placed in the same band.
 */
function getRecurringGroupKey(event: LifeEvent): string {
  if (isCashFlowEvent(event)) {
    return `cashflow_${event.direction}`;
  }
  if (isBuyAssetEvent(event)) {
    return `buy_${event.category}`;
  }
  if (isSellAssetEvent(event)) {
    return `sell_${event.category}`;
  }
  return `loan`;
}

/**
 * Compute the total pixel height of a type-band given its stack size.
 * In compact mode bars overlap (only peek px visible per extra bar).
 * In expanded mode each bar gets its own row with a small gap.
 */
function typeBandHeight(stackSize: number, compact: boolean): number {
  if (stackSize <= 0) return 0;
  if (compact) {
    return RECURRING_BAR_HEIGHT + (stackSize - 1) * RECURRING_STACK_PEEK;
  }
  return (
    stackSize * RECURRING_BAR_HEIGHT +
    Math.max(0, stackSize - 1) * RECURRING_INTRA_GAP
  );
}

/**
 * Pack recurring life events into rows grouped by type.
 *
 * Events of the same type are sorted by start age.
 * - Expanded (compact=false): each event gets its own full-height row.
 * - Compact (compact=true): bars overlap with only RECURRING_STACK_PEEK px
 *   showing per additional bar.
 * Oldest events sit highest (furthest from the chart), newest closest.
 * Different type bands are separated by RECURRING_ROW_GAP.
 */
function packRecurringEventsIntoRows(
  events: LifeEvent[],
  currentAge: number,
  currentYear: number,
  fallbackEndYear: number,
  retirementYear: number,
  pensionYear?: number,
  compact?: boolean
): RecurringBarInfo[] {
  const isCompact = compact ?? false;

  interface Candidate {
    event: LifeEvent;
    startAge: number;
    endAge: number;
    color: string;
    groupKey: string;
  }
  const candidates: Candidate[] = [];
  for (const event of events) {
    if (!event.enabled || !isRecurringEvent(event)) continue;
    const startAge = currentAge + (event.startYear - currentYear);
    const effectiveEndYear = getEffectiveEndYear(
      event,
      fallbackEndYear,
      retirementYear,
      pensionYear
    );
    const endAge = currentAge + (effectiveEndYear - currentYear);
    candidates.push({
      event,
      startAge,
      endAge,
      color: getEventColor(event),
      groupKey: getRecurringGroupKey(event),
    });
  }

  // Group by typeKey
  const byType = new Map<string, Candidate[]>();
  for (const c of candidates) {
    const arr = byType.get(c.groupKey) ?? [];
    arr.push(c);
    byType.set(c.groupKey, arr);
  }

  const result: RecurringBarInfo[] = [];

  const bandEntries: { typeKey: string; group: Candidate[] }[] = [];
  for (const [typeKey, group] of byType) {
    group.sort((a, b) => a.startAge - b.startAge);
    bandEntries.push({ typeKey, group });
  }

  // Stack bands upward from the chart.  Within each band, oldest events
  // sit highest (furthest from chart), newest sit closest.
  const baseOffset = RECURRING_LANE_TOP_PAD + 16;
  let cumulativeY = baseOffset;

  for (const { typeKey, group } of bandEntries) {
    const stackSize = group.length;
    const bandH = typeBandHeight(stackSize, isCompact);

    for (let i = 0; i < group.length; i++) {
      const c = group[i];
      const reversedIndex = stackSize - 1 - i;

      // Compact: bars overlap, only peek px between each
      // Expanded: each bar gets its own full row with a small gap
      const perBarStep = isCompact
        ? RECURRING_STACK_PEEK
        : RECURRING_BAR_HEIGHT + RECURRING_INTRA_GAP;
      const barY = -(
        cumulativeY +
        reversedIndex * perBarStep +
        RECURRING_BAR_HEIGHT
      );

      result.push({
        event: c.event,
        startAge: c.startAge,
        endAge: c.endAge,
        color: c.color,
        stackIndex: i,
        stackSize,
        typeKey,
        barY,
      });
    }

    cumulativeY += bandH + RECURRING_ROW_GAP;
  }

  return result;
}

/**
 * Custom nivo layer that renders life event markers on the projection curve.
 * Badges sit directly on the P50 (or deterministic) line; tooltips appear above.
 */
function LifeEventMarkersLayer({
  groups,
  xScale,
  yScale,
  chartData,
  showMonteCarlo,
  innerHeight,
  hoveredGroup,
  onHoverGroup,
  onLeaveGroup,
  eventScales,
  t,
  surface,
  tooltipBg,
  tooltipBorder,
  currSymbol,
  convertAmount = (v: number) => v,
}: {
  groups: EventMarkerGroup[];
  xScale: any;
  yScale: any;
  chartData: AdvancedRetirementGraphData[];
  showMonteCarlo: boolean;
  innerHeight: number;
  hoveredGroup: number | null;
  onHoverGroup: (index: number) => void;
  onLeaveGroup: () => void;
  eventScales: Map<string, number>;
  t: TranslationKey;
  surface: string;
  tooltipBg: string;
  tooltipBorder: string;
  currSymbol: string;
  convertAmount?: (v: number) => number;
}) {
  if (groups.length === 0) return null;

  // Pick the curve to place markers on: P50 when MC is on, deterministic otherwise
  const curveId = showMonteCarlo ? "P50" : t.chartView.projection;
  const curveSeries = chartData.find((d) => d.id === curveId);
  const curvePoints = curveSeries?.data ?? [];

  const flagSize = 28;
  const stackGap = 2; // vertical gap between stacked squares

  /**
   * Get a visual grouping key for an event.
   * Events with the same key are merged into one square with a count badge.
   */
  function getEventVisualKey(event: LifeEvent): string {
    if (isCashFlowEvent(event)) {
      return `cf-${event.direction}`;
    }
    if (isBuyAssetEvent(event)) {
      return `buy-${event.category}`;
    }
    if (isSellAssetEvent(event)) {
      return `sell-${event.category}`;
    }
    return `other-${(event as LifeEvent).id}`;
  }

  /**
   * Get the representative icon for a visual group of events.
   */
  const lifeEventIcons = getLifeEventIcons(t);
  const eventTypeInfo = getEventTypeInfo(t);
  const categoryInfo = getCategoryInfo(t);

  function getVisualGroupIcon(
    representative: LifeEvent,
    isHov: boolean,
    col: string
  ) {
    let iconElement: React.ReactNode = null;

    const iconKey = getEventIconKey(representative);
    if (iconKey && lifeEventIcons[iconKey]) {
      iconElement = lifeEventIcons[iconKey].icon;
    } else if (isCashFlowEvent(representative)) {
      iconElement =
        representative.direction === LifeEventDirection.INFLOW
          ? eventTypeInfo.inflow.icon
          : eventTypeInfo.outflow.icon;
    } else if (isBuyAssetEvent(representative)) {
      iconElement =
        categoryInfo[(representative as BuyAssetEvent).category]?.icon ||
        eventTypeInfo.buy.icon;
    } else if (isSellAssetEvent(representative)) {
      iconElement =
        categoryInfo[(representative as SellAssetEvent).category]?.icon ||
        eventTypeInfo.sell.icon;
    }

    if (!iconElement) return null;

    return (
      <div
        className="graph-event-icon"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          color: isHov ? "white" : col,
        }}
      >
        {iconElement}
      </div>
    );
  }

  // Helper to render a single event marker group
  const renderGroup = (group: EventMarkerGroup, i: number) => {
    const x = xScale(group.age);
    if (x === undefined || x === null) return null;

    // Get Y position on the curve
    const curveYValue = interpolateY(curvePoints, group.age);
    const curveY = curveYValue !== null ? yScale(curveYValue) : innerHeight / 2;

    const isHovered = hoveredGroup === i;

    // Group events by visual key for stacking
    const visualGroups: {
      key: string;
      events: LifeEvent[];
      representative: LifeEvent;
      color: string;
    }[] = [];
    const visualMap = new Map<string, number>();
    for (const evt of group.events) {
      const vk = getEventVisualKey(evt);
      const idx = visualMap.get(vk);
      if (idx !== undefined) {
        visualGroups[idx].events.push(evt);
      } else {
        visualMap.set(vk, visualGroups.length);
        visualGroups.push({
          key: vk,
          events: [evt],
          representative: evt,
          color: getEventColor(evt),
        });
      }
    }

    // Compute scale per visual group (max scale of its constituent events)
    const vgScales = visualGroups.map((vg) =>
      Math.max(...vg.events.map((e) => eventScales.get(e.id) ?? 1))
    );

    // Compute stacking positions with variable badge sizes (bottom to top)
    const scaledHeights = vgScales.map((s) => flagSize * s);
    const badgeYPositions: number[] = new Array(visualGroups.length);
    let nextBottom = curveY - 4; // 4px gap above the curve dot
    for (let vi = visualGroups.length - 1; vi >= 0; vi--) {
      badgeYPositions[vi] = nextBottom - scaledHeights[vi];
      nextBottom = badgeYPositions[vi] - stackGap;
    }

    // Use the primary event color for the dashed line / dot
    const primaryColor =
      visualGroups.length === 1 ? visualGroups[0].color : "#5C6BC0";

    return (
      <g
        key={i}
        transform={`translate(${x}, 0)`}
        onMouseEnter={() => onHoverGroup(i)}
        onMouseLeave={onLeaveGroup}
        style={{ cursor: "pointer" }}
      >
        {/* Vertical dashed line from badge down to x-axis */}
        <line
          x1={0}
          y1={curveY}
          x2={0}
          y2={innerHeight}
          stroke={primaryColor}
          strokeWidth={isHovered ? 1.5 : 1}
          strokeDasharray="3 3"
          opacity={isHovered ? 0.6 : 0.25}
        />

        {/* Dot on the curve */}
        <circle
          cx={0}
          cy={curveY}
          r={isHovered ? 5 : 3.5}
          fill={primaryColor}
          stroke={surface}
          strokeWidth={2}
        />

        {/* Stacked flag badges above the curve point (scaled by monetary worth) */}
        {visualGroups.map((vg, vi) => {
          const col = vg.color;
          const count = vg.events.length;
          const scale = vgScales[vi];
          const badgeY = badgeYPositions[vi];
          const scaledH = scaledHeights[vi];

          return (
            // Scale transform: render badge at base flagSize, visually scale to scaledH
            // Transform chain: move center to badgeY+scaledH/2 → scale → recenter at base
            <g
              key={vg.key}
              transform={`translate(0, ${
                badgeY + scaledH / 2
              }) scale(${scale}) translate(0, ${-flagSize / 2})`}
            >
              <rect
                x={-flagSize / 2}
                y={0}
                width={flagSize}
                height={flagSize}
                rx={4}
                fill={isHovered ? col : surface}
                stroke={col}
                strokeWidth={1.5}
              />

              {/* Event icon */}
              <foreignObject x={-11} y={3} width={22} height={22}>
                {getVisualGroupIcon(vg.representative, isHovered, col)}
              </foreignObject>

              {/* Count badge on top-right corner when >1 events of same type */}
              {count > 1 && (
                <g transform={`translate(${flagSize / 2 - 2}, -3)`}>
                  <circle
                    cx={0}
                    cy={0}
                    r={7}
                    fill={col}
                    stroke={surface}
                    strokeWidth={1}
                  />
                  <text
                    x={0}
                    y={3.5}
                    textAnchor="middle"
                    fontSize={9}
                    fontWeight="bold"
                    fill={surface}
                  >
                    {count}
                  </text>
                </g>
              )}

              {/* Recurring badge on top-left corner when group has a recurring event */}
              {vg.events.some(isRecurringEvent) && (
                <g transform={`translate(${-flagSize / 2 + 2}, -3)`}>
                  <circle
                    cx={0}
                    cy={0}
                    r={7}
                    fill={isHovered ? surface : col}
                    stroke={isHovered ? col : surface}
                    strokeWidth={1}
                  />
                  {/* Repeat / sync icon (two curved arrows) */}
                  <svg
                    x={-5}
                    y={-5}
                    width={10}
                    height={10}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={isHovered ? col : surface}
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 1l4 4-4 4" />
                    <path d="M3 11V9a4 4 0 014-4h14" />
                    <path d="M7 23l-4-4 4-4" />
                    <path d="M21 13v2a4 4 0 01-4 4H3" />
                  </svg>
                </g>
              )}
            </g>
          );
        })}

        {/* Tooltip on hover — positioned above the stacked icons */}
        {isHovered &&
          (() => {
            const tooltipH = Math.min(group.events.length, 6) * 26 + 20;
            // Place tooltip above the full stack; clamp so it doesn't go above chart
            const topOfStack = badgeYPositions[0]; // topmost badge Y
            const tooltipY = Math.max(-20, topOfStack - tooltipH - 6);
            return (
              <foreignObject
                x={-80}
                y={tooltipY}
                width={240}
                height={tooltipH + 8}
                style={{ overflow: "visible" }}
              >
                <div
                  style={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                    padding: "8px 12px",
                    direction: "rtl",
                    fontSize: 12,
                  }}
                >
                  {group.events.slice(0, 6).map((evt, j) => {
                    const evtColor = getEventColor(evt);
                    const freq =
                      evt.frequency === LifeEventFrequency.ONE_TIME
                        ? t.events.oneTime
                        : evt.frequency === LifeEventFrequency.MONTHLY
                        ? t.events.monthly
                        : t.events.yearly;
                    const annualizedAmount =
                      evt.frequency === LifeEventFrequency.MONTHLY
                        ? evt.amount * 12
                        : evt.amount;
                    return (
                      <div
                        key={j}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "3px 0",
                          borderBottom:
                            j < Math.min(group.events.length, 6) - 1
                              ? "1px solid #eee"
                              : "none",
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: evtColor,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {evt.name}
                        </span>
                        <span style={{ color: "#888", whiteSpace: "nowrap" }}>
                          {currSymbol}
                          {Math.round(
                            convertAmount(annualizedAmount)
                          ).toLocaleString()}{" "}
                          · {freq}
                        </span>
                      </div>
                    );
                  })}
                  {group.events.length > 6 && (
                    <div
                      style={{
                        color: "#888",
                        textAlign: "center",
                        paddingTop: 4,
                        fontSize: 11,
                      }}
                    >
                      +{group.events.length - 6} {t.chartView.moreEvents}
                    </div>
                  )}
                </div>
              </foreignObject>
            );
          })()}
      </g>
    );
  };

  // Render non-hovered groups first, then the hovered group last
  // so the tooltip always renders on top of neighboring icons (SVG z-order = DOM order)
  return (
    <g>
      {/* Global style to scale icons inside graph event markers */}
      <foreignObject width={0} height={0} style={{ overflow: "visible" }}>
        <style>{`
          .graph-event-icon svg {
            width: 20px !important;
            height: 20px !important;
          }
          .graph-event-icon .MuiSvgIcon-root {
            font-size: 20px !important;
            width: 20px !important;
            height: 20px !important;
          }
        `}</style>
      </foreignObject>
      {groups.map((group, i) =>
        hoveredGroup !== i ? renderGroup(group, i) : null
      )}
      {hoveredGroup !== null &&
        hoveredGroup < groups.length &&
        renderGroup(groups[hoveredGroup], hoveredGroup)}
    </g>
  );
}

// ============================================================================
// RECURRING EVENTS LANE LAYER
// ============================================================================

const RECURRING_BAR_HEIGHT = 18;
const RECURRING_ROW_GAP = 3;
const RECURRING_LANE_TOP_PAD = 4; // padding between last bar and milestone labels

/**
 * Custom nivo layer that renders recurring life events as horizontal duration bars
 * inside the chart area near the bottom, with dashed lines and dots connecting
 * each bar's start/end to the projection curve.
 */
function RecurringEventsLaneLayer({
  bars,
  xScale,
  yScale,
  chartData,
  showMonteCarlo,
  innerHeight,
  hoveredBar,
  onHoverBar,
  onLeaveBar,
  showLines,
  compact,
  t,
  surface,
  tooltipBg,
  tooltipBorder,
  currSymbol,
  convertAmount = (v: number) => v,
}: {
  bars: RecurringBarInfo[];
  xScale: any;
  yScale: any;
  chartData: AdvancedRetirementGraphData[];
  showMonteCarlo: boolean;
  innerHeight: number;
  hoveredBar: string | null;
  onHoverBar: (id: string) => void;
  onLeaveBar: () => void;
  showLines: boolean;
  compact: boolean;
  t: TranslationKey;
  surface: string;
  tooltipBg: string;
  tooltipBorder: string;
  currSymbol: string;
  convertAmount?: (v: number) => number;
}) {
  if (bars.length === 0) return null;

  // Pick the curve to connect dots to: P50 when MC is on, deterministic otherwise
  const curveId = showMonteCarlo ? "P50" : t.chartView.projection;
  const curveSeries = chartData.find((d) => d.id === curveId);
  const curvePoints = curveSeries?.data ?? [];

  return (
    <g>
      {/* CSS for small icons inside recurring bars */}
      <foreignObject width={0} height={0} style={{ overflow: "visible" }}>
        <style>{`
          .graph-recurring-icon svg {
            width: 12px !important;
            height: 12px !important;
          }
          .graph-recurring-icon .MuiSvgIcon-root {
            font-size: 12px !important;
            width: 12px !important;
            height: 12px !important;
          }
        `}</style>
      </foreignObject>
      {/* Render non-hovered bars first, then hovered on top (SVG z-order) */}
      {bars.map((bar) =>
        hoveredBar !== bar.event.id
          ? renderRecurringBar(
              bar,
              innerHeight,
              xScale,
              yScale,
              curvePoints,
              false,
              onHoverBar,
              onLeaveBar,
              showLines,
              compact,
              t,
              surface,
              tooltipBg,
              tooltipBorder,
              currSymbol,
              convertAmount
            )
          : null
      )}
      {hoveredBar != null &&
        bars.map((bar) =>
          hoveredBar === bar.event.id
            ? renderRecurringBar(
                bar,
                innerHeight,
                xScale,
                yScale,
                curvePoints,
                true,
                onHoverBar,
                onLeaveBar,
                showLines,
                compact,
                t,
                surface,
                tooltipBg,
                tooltipBorder,
                currSymbol,
                convertAmount
              )
            : null
        )}
    </g>
  );
}

/**
 * Render a single recurring event bar with connecting dashed lines and dots
 * to the projection curve at the start and end ages.
 */
function renderRecurringBar(
  bar: RecurringBarInfo,
  innerHeight: number,
  xScale: any,
  yScale: any,
  curvePoints: { x: number; y: number }[],
  isHovered: boolean,
  onHoverBar: (id: string) => void,
  onLeaveBar: () => void,
  showLines: boolean,
  compact: boolean,
  t: TranslationKey,
  surface: string,
  tooltipBg: string,
  tooltipBorder: string,
  currSymbol: string,
  convertAmount: (v: number) => number = (v) => v
) {
  const lifeEventIcons = getLifeEventIcons(t);
  const eventTypeInfo = getEventTypeInfo(t);
  const categoryInfo = getCategoryInfo(t);

  const x1 = xScale(bar.startAge);
  const x2 = xScale(bar.endAge);
  const barWidth = Math.max(x2 - x1, 6);
  const barY = bar.barY;
  const barBottom = barY + RECURRING_BAR_HEIGHT;
  const col = bar.color;

  // Find the curve Y positions at the start and end of the bar
  const startCurveVal = interpolateY(curvePoints, bar.startAge);
  const endCurveVal = interpolateY(curvePoints, bar.endAge);
  const startCurveY = startCurveVal !== null ? yScale(startCurveVal) : null;
  const endCurveY = endCurveVal !== null ? yScale(endCurveVal) : null;

  // Resolve the event icon
  const iconKey = getEventIconKey(bar.event);
  let iconElement: React.ReactNode = null;
  if (iconKey && lifeEventIcons[iconKey]) {
    iconElement = lifeEventIcons[iconKey].icon;
  } else if (isCashFlowEvent(bar.event)) {
    iconElement =
      bar.event.direction === LifeEventDirection.INFLOW
        ? eventTypeInfo.inflow.icon
        : eventTypeInfo.outflow.icon;
  } else if (isBuyAssetEvent(bar.event)) {
    iconElement =
      categoryInfo[(bar.event as BuyAssetEvent).category]?.icon ||
      eventTypeInfo.buy.icon;
  } else if (isSellAssetEvent(bar.event)) {
    iconElement =
      categoryInfo[(bar.event as SellAssetEvent).category]?.icon ||
      eventTypeInfo.sell.icon;
  }

  // Frequency label for tooltip
  const freq =
    bar.event.frequency === LifeEventFrequency.MONTHLY
      ? t.events.monthly
      : t.events.yearly;

  // Only show text label if the bar is wide enough
  const showLabel = barWidth > 40;
  // Show connecting lines when the toggle is on, or always when hovering a specific bar
  const showConnectors = showLines || isHovered;

  return (
    <g
      key={bar.event.id}
      onMouseEnter={() => onHoverBar(bar.event.id)}
      onMouseLeave={onLeaveBar}
      style={{ cursor: "pointer" }}
    >
      {/* Solid line from bottom of bar down to curve at start age */}
      {showConnectors && startCurveY != null && startCurveY > barBottom && (
        <line
          x1={x1}
          y1={barBottom}
          x2={x1}
          y2={startCurveY}
          stroke={col}
          strokeWidth={isHovered ? 1.5 : 1}
          opacity={isHovered ? 0.7 : 0.35}
        />
      )}

      {/* Solid line from bottom of bar down to curve at end age */}
      {showConnectors && endCurveY != null && endCurveY > barBottom && (
        <line
          x1={x1 + barWidth}
          y1={barBottom}
          x2={x1 + barWidth}
          y2={endCurveY}
          stroke={col}
          strokeWidth={isHovered ? 1.5 : 1}
          opacity={isHovered ? 0.7 : 0.35}
        />
      )}

      {/* Dot on the curve at start age */}
      {showConnectors && startCurveY != null && (
        <circle
          cx={x1}
          cy={startCurveY}
          r={isHovered ? 4 : 3}
          fill={col}
          stroke={surface}
          strokeWidth={1.5}
          opacity={isHovered ? 1 : 0.7}
        />
      )}

      {/* Dot on the curve at end age */}
      {showConnectors && endCurveY != null && (
        <circle
          cx={x1 + barWidth}
          cy={endCurveY}
          r={isHovered ? 4 : 3}
          fill={col}
          stroke={surface}
          strokeWidth={1.5}
          opacity={isHovered ? 1 : 0.7}
        />
      )}

      {/* Bar background */}
      <rect
        x={x1}
        y={barY}
        width={barWidth}
        height={RECURRING_BAR_HEIGHT}
        rx={4}
        fill={isHovered ? col : surface}
        stroke={col}
        strokeWidth={isHovered ? 2 : 1}
        opacity={isHovered ? 1 : 0.9}
        filter={
          isHovered ? "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" : undefined
        }
      />

      {/* Icon and optional label inside bar */}
      <foreignObject
        x={x1}
        y={barY}
        width={barWidth}
        height={RECURRING_BAR_HEIGHT}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            height: "100%",
            direction: "rtl",
            overflow: "hidden",
            padding: "0 4px",
          }}
        >
          {iconElement && (
            <div
              className="graph-recurring-icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 14,
                height: 14,
                color: isHovered ? "white" : col,
                flexShrink: 0,
              }}
            >
              {iconElement}
            </div>
          )}
          {showLabel && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: isHovered ? "white" : col,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: `${RECURRING_BAR_HEIGHT}px`,
              }}
            >
              {bar.event.name}
            </span>
          )}
        </div>
      </foreignObject>

      {/* Stack count badge — only in compact mode where bars overlap */}
      {compact &&
        bar.stackSize > 1 &&
        bar.stackIndex === bar.stackSize - 1 &&
        !isHovered && (
          <g>
            <circle
              cx={x1 + barWidth - 2}
              cy={barY + 2}
              r={6}
              fill={col}
              stroke={surface}
              strokeWidth={1}
            />
            <text
              x={x1 + barWidth - 2}
              y={barY + 2}
              textAnchor="middle"
              dominantBaseline="central"
              fill={surface}
              fontSize={8}
              fontWeight={700}
            >
              {bar.stackSize}
            </text>
          </g>
        )}

      {/* Tooltip on hover — positioned below the bar (inside chart area) */}
      {isHovered && (
        <foreignObject
          x={Math.max(0, (x1 + x1 + barWidth) / 2 - 100)}
          y={barBottom + 4}
          width={200}
          height={50}
          style={{ overflow: "visible" }}
        >
          <div
            style={{
              background: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
              padding: "6px 10px",
              direction: "rtl",
              fontSize: 11,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              {bar.event.name}
            </div>
            <div style={{ color: "#666" }}>
              {currSymbol}
              {Math.round(
                convertAmount(
                  bar.event.frequency === LifeEventFrequency.MONTHLY
                    ? bar.event.amount * 12
                    : bar.event.amount
                )
              ).toLocaleString()}{" "}
              · {freq} · {t.chartView.age} {bar.startAge}–{bar.endAge}
            </div>
          </div>
        </foreignObject>
      )}
    </g>
  );
}

/**
 * Format projection data for Nivo chart
 * X-axis uses age instead of years from now
 */
function formatProjectionData(
  projection: ProjectionYearPoint[],
  currentAge: number,
  monteCarlo?: MonteCarloResult,
  showMonteCarlo?: boolean,
  projectionLabel?: string
): AdvancedRetirementGraphData[] {
  const currentYear = new Date().getFullYear();
  const data: AdvancedRetirementGraphData[] = [];

  // Deterministic projection line — only shown when Monte Carlo is off (y can be negative when in debt)
  if (!monteCarlo || !showMonteCarlo) {
    data.push({
      id: projectionLabel || "Projection",
      color: BRAND_SECONDARY_RGB,
      data: projection.map((point) => ({
        x: currentAge + (point.year - currentYear), // Use age
        y: point.portfolio,
      })),
    });
  }

  // Monte Carlo bands (y can be negative when in debt)
  if (monteCarlo && showMonteCarlo) {
    // P90 (optimistic)
    data.push({
      id: "P90",
      color: "rgba(76, 175, 80, 0.3)",
      data: monteCarlo.percentiles.map((p) => ({
        x: currentAge + (p.year - currentYear), // Use age
        y: p.p90,
      })),
    });

    // P50 (median)
    data.push({
      id: "P50",
      color: BRAND_PRIMARY_RGB,
      data: monteCarlo.percentiles.map((p) => ({
        x: currentAge + (p.year - currentYear), // Use age
        y: p.p50,
      })),
    });

    // P10 (pessimistic)
    data.push({
      id: "P10",
      color: "rgba(244, 67, 54, 0.3)",
      data: monteCarlo.percentiles.map((p) => ({
        x: currentAge + (p.year - currentYear), // Use age
        y: p.p10,
      })),
    });
  }

  return data;
}

/**
 * Find minimum point for chart scaling
 */
function findMinPoint(data: AdvancedRetirementGraphData[]): number {
  let min = 0;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.y < min) {
        min = point.y;
      }
    });
  });
  return min;
}

/**
 * Find maximum point for chart scaling
 */
function findMaxPoint(data: AdvancedRetirementGraphData[]): number {
  let max = 0;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.y > max) {
        max = point.y;
      }
    });
  });
  return max;
}

/**
 * Calculate smart Y-axis maximum
 * - When Monte Carlo is shown: min(P50_max + 30%, P90_max)
 * - Otherwise: deterministic max + 15%
 */
/** Zoom step multipliers: level 0 = no zoom, level 4 = most zoomed in */
const ZOOM_FACTORS = [1.0, 0.8, 0.6, 0.45, 0.3];

/** Minimum chart dimensions so Nivo inner width/height stay positive. Avoids negative <rect> and freeze when tab/layout changes. */
const MIN_CHART_WIDTH = 100;
const MIN_CHART_HEIGHT = 100;

function calculateSmartYMax(
  data: AdvancedRetirementGraphData[],
  monteCarlo?: MonteCarloResult,
  showMonteCarlo?: boolean,
  projectionLabel?: string,
  eventBarsTotalHeight?: number,
  zoomLevel?: number
): number {
  // Find deterministic line max (first data series is always deterministic)
  const deterministicData = data.find(
    (d) => d.id === (projectionLabel || "Projection")
  );
  const deterministicMax = deterministicData
    ? Math.max(...deterministicData.data.map((p) => p.y))
    : findMaxPoint(data);

  // When recurring event bars take up space above the chart, compress the Y-axis
  // so the graph data fills the remaining visible area better.
  // Scale the padding multiplier down aggressively as event height grows.
  const eventHeight = eventBarsTotalHeight ?? 0;
  // At 0 event height → multiplier 1.3; drops fast, clamp to 1.02
  const paddingMultiplier = Math.max(1.02, 1.3 - eventHeight * 0.008);

  let baseMax: number;

  // If no Monte Carlo or not showing it, use deterministic-based max
  if (!monteCarlo || !showMonteCarlo) {
    baseMax = deterministicMax * Math.max(1.02, 1.15 - eventHeight * 0.004);
  } else {
    // Find max P50 and P90 across all years
    const maxP50 = Math.max(...monteCarlo.percentiles.map((p) => p.p50));
    const maxP90 = Math.max(...monteCarlo.percentiles.map((p) => p.p90));

    // Y-axis max = min(P50 * multiplier, P90)
    const p50Padded = maxP50 * paddingMultiplier;
    baseMax = Math.min(p50Padded, maxP90);
  }

  // Apply zoom factor
  const zoom = zoomLevel ?? 0;
  const factor = ZOOM_FACTORS[Math.min(zoom, ZOOM_FACTORS.length - 1)];
  return baseMax * factor;
}

/**
 * Generate tick values for X axis (ages)
 */
function generateAgeTicks(
  currentAge: number,
  lifeExpectancy: number
): number[] {
  const tickValues: number[] = [];
  const ageRange = lifeExpectancy - currentAge;
  const interval = Math.ceil(ageRange / 7);

  // Start from current age rounded to nearest 5
  const startAge = Math.floor(currentAge / 5) * 5;

  for (let age = startAge; age <= lifeExpectancy; age += interval) {
    tickValues.push(age);
  }

  // Always include life expectancy if not already there
  if (!tickValues.includes(lifeExpectancy)) {
    tickValues.push(lifeExpectancy);
  }

  return tickValues;
}

export const AdvancedRetirementGraph: React.FC<
  AdvancedRetirementGraphProps
> = ({
  deterministicProjection,
  monteCarlo,
  fireNumber,
  fireYear,
  retirementAge,
  pensionEligibilityAge,
  currentAge,
  lifeExpectancy,
  showMonteCarlo,
  lifeEvents = [],
  goals = [],
  fullscreen = false,
  hideControls = false,
}) => {
  const { lang } = useLanguage();
  const {
    symbol: currencySymbol,
    convert: convertCurr,
    unconvert: unconvertCurr,
  } = useCurrency();
  const t = translations[lang];

  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);
  const [hoveredRecurringBar, setHoveredRecurringBar] = useState<string | null>(
    null
  );
  const [showDurationBars, setShowDurationBars] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [compactEvents, setCompactEvents] = useState(true);
  const [showEventLines, setShowEventLines] = useState(true);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const viewMenuOpen = Boolean(viewMenuAnchor);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartSizeReady, setChartSizeReady] = useState(false);
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const check = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setChartSizeReady(w >= MIN_CHART_WIDTH && h >= MIN_CHART_HEIGHT);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsDark(document.documentElement.classList.contains("dark"));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  const surface = isDark ? "#1e1e2e" : "white";
  const tooltipBg = isDark ? "#2a2a3e" : "white";
  const tooltipBorder = isDark ? "#3e3e56" : "#ccc";

  const dataGraphs = formatProjectionData(
    deterministicProjection,
    currentAge,
    monteCarlo,
    showMonteCarlo,
    t.chartView.projection
  );
  const tickValues = generateAgeTicks(currentAge, lifeExpectancy);
  const currentYear = new Date().getFullYear();

  // Milestone years for resolving endYearMode on life events
  const fallbackEndYear = currentYear + (lifeExpectancy - currentAge);
  const retirementYear = currentYear + (retirementAge - currentAge);
  const pensionYear =
    currentYear + ((pensionEligibilityAge ?? retirementAge) - currentAge);

  // Compute per-event scale factors based on monetary worth
  const eventScales = computeEventScaleFactors(
    lifeEvents,
    fallbackEndYear,
    retirementYear,
    pensionYear
  );

  // Check if there are any recurring events (to decide whether to show the toggle)
  const hasRecurringEvents = lifeEvents.some(
    (e) => e.enabled && isRecurringEvent(e)
  );

  // Group life events for chart markers
  // When duration bars are ON, recurring events are excluded from point markers
  // When OFF, all events show as point markers (classic mode)
  const ageRange = lifeExpectancy - currentAge;
  const minAgeGap = Math.max(2, Math.round(ageRange / 20));
  const eventGroups = groupLifeEvents(
    lifeEvents,
    currentAge,
    currentYear,
    minAgeGap,
    showDurationBars
  );

  // Compute recurring event bars with stacked-by-type layout (only when toggle is ON)
  const recurringBars = showDurationBars
    ? packRecurringEventsIntoRows(
        lifeEvents,
        currentAge,
        currentYear,
        fallbackEndYear,
        retirementYear,
        pensionYear,
        compactEvents
      )
    : [];

  // Dynamic top margin: the highest bar's top Y is the most negative barY value
  const eventBarsTotalHeight =
    recurringBars.length > 0
      ? Math.abs(Math.min(...recurringBars.map((b) => b.barY)))
      : 0;
  const topMargin = eventBarsTotalHeight > 0 ? eventBarsTotalHeight + 4 : 20;

  // Cap negative Y so it doesn't dominate: min is at most -10% of the main projection line max
  const mainSeriesId = showMonteCarlo
    ? "P50"
    : t.chartView.projection || "Projection";
  const mainSeries = dataGraphs.find((d) => d.id === mainSeriesId);
  const maxOfGreenLine =
    mainSeries && mainSeries.data.length > 0
      ? Math.max(...mainSeries.data.map((p) => p.y))
      : findMaxPoint(dataGraphs);
  const yMinFloor = maxOfGreenLine > 0 ? -0.1 * maxOfGreenLine : 0;
  const rawYMin = Math.min(
    0,
    findMinPoint(dataGraphs) - Math.abs(findMinPoint(dataGraphs) / 20)
  );
  const yMinILS = Math.max(yMinFloor, rawYMin);
  const yMaxILS = calculateSmartYMax(
    dataGraphs,
    monteCarlo,
    showMonteCarlo,
    t.chartView.projection,
    eventBarsTotalHeight,
    zoomLevel
  );
  const yTickValues = computeNiceYTicks(
    convertCurr(yMinILS),
    convertCurr(yMaxILS),
    unconvertCurr,
    6
  );

  // Custom milestone lines layer — draws labels above and dashed lines below
  const MilestoneLinesLayer = ({ xScale, innerHeight }: any) => {
    const retX = xScale(retirementAge);
    const penX =
      pensionEligibilityAge != null ? xScale(pensionEligibilityAge) : null;
    const retTextY = -6; // above the chart area (within top margin)
    const retLineStartY = 1; // start the dashed line below the label
    const penTextY = 18; // pension label sits ~24px lower than retirement label
    const penLineStartY = 26; // pension dashed line starts below its label

    // Hide the planned retirement line if it's the same age as pension eligibility
    const hideRetirementLine =
      pensionEligibilityAge != null && retirementAge === pensionEligibilityAge;

    return (
      <g>
        {/* Early retirement line — hidden when same as pension eligibility age */}
        {!hideRetirementLine && retX != null && !isNaN(retX) && (
          <>
            <text
              x={retX}
              y={retTextY}
              textAnchor="middle"
              fontSize={10}
              fill="#2196F3"
            >
              {t.chartView.earlyRetirement}
            </text>
            <line
              x1={retX}
              y1={retLineStartY}
              x2={retX}
              y2={innerHeight}
              stroke="#2196F3"
              strokeWidth={2}
              strokeDasharray="4 4"
            />
          </>
        )}
        {/* Pension eligibility line — always positioned lower to avoid overlap */}
        {penX != null && !isNaN(penX) && (
          <>
            <text
              x={penX}
              y={penTextY}
              textAnchor="middle"
              fontSize={10}
              fill="#00BCD4"
            >
              {t.chartView.pension}
            </text>
            <line
              x1={penX}
              y1={penLineStartY}
              x2={penX}
              y2={innerHeight}
              stroke="#00BCD4"
              strokeWidth={2}
              strokeDasharray="4 4"
            />
          </>
        )}
      </g>
    );
  };

  // Goal target markers layer — renders dashed lines for PORTFOLIO_TARGET and MINIMUM_FLOOR goals
  const enabledGoals = goals.filter((g) => g.enabled);
  const GoalMarkersLayer = ({ xScale, yScale, innerHeight }: any) => {
    if (enabledGoals.length === 0) return null;

    const goalColors = ["#9C27B0", "#FF5722", "#795548", "#607D8B"];

    return (
      <g>
        {enabledGoals.map((goal, idx) => {
          const color = goalColors[idx % goalColors.length];

          // Skip PURCHASE_GOAL — it's shown as a life event marker instead
          if (goal.kind === RetirementGoalKind.PURCHASE_GOAL) return null;

          // Horizontal line for amount-based goals
          if (
            (goal.kind === RetirementGoalKind.PORTFOLIO_TARGET ||
              goal.kind === RetirementGoalKind.MINIMUM_FLOOR) &&
            goal.targetAmount != null
          ) {
            const y = yScale(goal.targetAmount);
            if (y == null || isNaN(y) || y < 0) return null;

            // For PORTFOLIO_TARGET with a target age, also draw a vertical marker
            const hasVertical =
              goal.kind === RetirementGoalKind.PORTFOLIO_TARGET &&
              goal.targetAge != null;
            const vx = hasVertical ? xScale(goal.targetAge!) : null;

            return (
              <g key={goal.id}>
                {/* Horizontal target amount line */}
                <line
                  x1={0}
                  y1={y}
                  x2={hasVertical && vx != null ? vx : xScale(lifeExpectancy)}
                  y2={y}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="6 3"
                  opacity={0.7}
                />
                <text
                  x={4}
                  y={y - 5}
                  fill={color}
                  fontSize={9}
                  fontWeight={600}
                  opacity={0.85}
                >
                  {goal.name}
                </text>
                {/* Vertical target age marker for PORTFOLIO_TARGET */}
                {hasVertical && vx != null && !isNaN(vx) && (
                  <line
                    x1={vx}
                    y1={y}
                    x2={vx}
                    y2={innerHeight}
                    stroke={color}
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    opacity={0.5}
                  />
                )}
                {/* Circle at intersection for PORTFOLIO_TARGET */}
                {hasVertical && vx != null && !isNaN(vx) && (
                  <circle cx={vx} cy={y} r={4} fill={color} opacity={0.8} />
                )}
              </g>
            );
          }

          // Vertical line for age-based goals without amount (FIRE_BY_AGE)
          if (
            goal.kind === RetirementGoalKind.FIRE_BY_AGE &&
            goal.targetAge != null
          ) {
            const x = xScale(goal.targetAge);
            if (x == null || isNaN(x)) return null;
            return (
              <g key={goal.id}>
                <line
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={innerHeight}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="6 3"
                  opacity={0.6}
                />
                <text
                  x={x + 4}
                  y={14}
                  fill={color}
                  fontSize={9}
                  fontWeight={600}
                  opacity={0.85}
                >
                  {goal.name}
                </text>
              </g>
            );
          }

          return null;
        })}
      </g>
    );
  };

  return (
    <div
      className={
        fullscreen
          ? "flex h-full w-full min-w-[200px] flex-col"
          : "w-full min-w-[200px]"
      }
    >
      {!hideControls && (
        <div className="mb-2 flex justify-end border-b border-border-subtle pb-2">
          <button
            type="button"
            onClick={(e) => setViewMenuAnchor(e.currentTarget)}
            className="inline-flex items-center gap-0.5 rounded-md px-1 py-0.5 text-xs text-menu/70 transition-colors hover:bg-surface-subtle"
          >
            {t.chartView.viewMenu}
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </button>

          <Menu
            anchorEl={viewMenuAnchor}
            open={viewMenuOpen}
            onClose={() => setViewMenuAnchor(null)}
            minWidth={220}
            className="p-2.5"
          >
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between gap-3 px-1">
                <span className="whitespace-nowrap text-xs text-menu/70">
                  {t.chartView.zoom}
                </span>
                <div className="flex items-center gap-0.5">
                  <Tooltip content={t.chartView.zoomOut}>
                    <IconButton
                      aria-label={t.chartView.zoomOut}
                      size="sm"
                      variant="ghost"
                      onClick={() => setZoomLevel((z) => Math.max(0, z - 1))}
                      disabled={zoomLevel <= 0}
                      icon={<ZoomOutIcon sx={{ fontSize: 16 }} />}
                    />
                  </Tooltip>
                  <span className="min-w-[24px] select-none text-center text-[10px] text-menu/50">
                    {zoomLevel > 0 ? zoomLevel : "—"}
                  </span>
                  <Tooltip content={t.chartView.zoomIn}>
                    <IconButton
                      aria-label={t.chartView.zoomIn}
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setZoomLevel((z) =>
                          Math.min(ZOOM_FACTORS.length - 1, z + 1)
                        )
                      }
                      disabled={zoomLevel >= ZOOM_FACTORS.length - 1}
                      icon={<ZoomInIcon sx={{ fontSize: 16 }} />}
                    />
                  </Tooltip>
                </div>
              </div>

              {hasRecurringEvents && showDurationBars && (
                <div
                  className="flex items-center justify-between gap-3 px-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs text-menu/70">
                    {compactEvents
                      ? t.chartView.expandEvents
                      : t.chartView.shrinkEvents}
                  </span>
                  <Toggle
                    size="sm"
                    checked={compactEvents}
                    onChange={setCompactEvents}
                    aria-label={
                      compactEvents
                        ? t.chartView.expandEvents
                        : t.chartView.shrinkEvents
                    }
                  />
                </div>
              )}

              {hasRecurringEvents && showDurationBars && (
                <div
                  className="flex items-center justify-between gap-3 px-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs text-menu/70">
                    {showEventLines
                      ? t.chartView.hideLines
                      : t.chartView.showLines}
                  </span>
                  <Toggle
                    size="sm"
                    checked={showEventLines}
                    onChange={setShowEventLines}
                    aria-label={
                      showEventLines
                        ? t.chartView.hideLines
                        : t.chartView.showLines
                    }
                  />
                </div>
              )}

              {hasRecurringEvents && (
                <div
                  className="flex items-center justify-between gap-3 px-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs text-menu/70">
                    {showDurationBars
                      ? t.chartView.normalView
                      : t.chartView.durationView}
                  </span>
                  <Toggle
                    size="sm"
                    checked={showDurationBars}
                    onChange={setShowDurationBars}
                    aria-label={
                      showDurationBars
                        ? t.chartView.normalView
                        : t.chartView.durationView
                    }
                  />
                </div>
              )}
            </div>
          </Menu>
        </div>
      )}
      <div
        ref={chartContainerRef}
        className={
          fullscreen
            ? "min-h-0 flex-1"
            : "h-[400px] min-h-[300px] w-full mobile:h-[350px]"
        }
      >
        {chartSizeReady && (
          <ResponsiveLine
            margin={{ top: topMargin, right: 10, bottom: 50, left: 50 }}
            animate={true}
            enableSlices="x"
            layers={[
              "grid",
              // Custom milestone lines (retirement + pension eligibility)
              MilestoneLinesLayer,
              // Goal target markers (dashed lines for amount targets and age targets)
              GoalMarkersLayer,
              "areas",
              "lines",
              "slices",
              "axes",
              "points",
              "legends",
              // Custom life events layer (one-time events as icon markers on the curve)
              ({ xScale, yScale, innerHeight }: any) => (
                <LifeEventMarkersLayer
                  key="life-events"
                  groups={eventGroups}
                  xScale={xScale}
                  yScale={yScale}
                  chartData={dataGraphs}
                  showMonteCarlo={!!showMonteCarlo}
                  innerHeight={innerHeight}
                  hoveredGroup={hoveredGroup}
                  onHoverGroup={setHoveredGroup}
                  onLeaveGroup={() => setHoveredGroup(null)}
                  eventScales={eventScales}
                  t={t}
                  surface={surface}
                  tooltipBg={tooltipBg}
                  tooltipBorder={tooltipBorder}
                  currSymbol={currencySymbol}
                  convertAmount={convertCurr}
                />
              ),
              // Recurring events duration bars inside chart, connected to the curve
              ({ xScale, yScale, innerHeight }: any) => (
                <RecurringEventsLaneLayer
                  key="recurring-events"
                  bars={recurringBars}
                  xScale={xScale}
                  yScale={yScale}
                  chartData={dataGraphs}
                  showMonteCarlo={!!showMonteCarlo}
                  innerHeight={innerHeight}
                  hoveredBar={hoveredRecurringBar}
                  onHoverBar={setHoveredRecurringBar}
                  onLeaveBar={() => setHoveredRecurringBar(null)}
                  showLines={showEventLines}
                  compact={compactEvents}
                  t={t}
                  surface={surface}
                  tooltipBg={tooltipBg}
                  tooltipBorder={tooltipBorder}
                  currSymbol={currencySymbol}
                  convertAmount={convertCurr}
                />
              ),
            ]}
            sliceTooltip={({ slice }) => {
              const age = Number(slice.points[0]?.data?.x || currentAge);
              const yearsFromNow = age - currentAge;
              return (
                <div
                  style={{
                    background: tooltipBg,
                    padding: "12px 16px",
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <strong>
                      {t.chartView.age} {age}
                      {yearsFromNow === 0
                        ? ` (${t.chartView.now})`
                        : yearsFromNow === 1
                        ? ` (${t.chartView.inOneYear})`
                        : ` (${t.chartView.inYearsPrefix} ${yearsFromNow} ${t.chartView.yearsSuffix})`}
                    </strong>
                  </div>
                  {slice.points.map((point) => (
                    <div
                      key={point.id}
                      style={{
                        color: point.serieColor,
                        padding: "3px 0",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "16px",
                      }}
                    >
                      <span>{point.serieId}</span>
                      <strong>{point.data.yFormatted}</strong>
                    </div>
                  ))}
                </div>
              );
            }}
            enablePoints={false}
            colors={(d) => d.color}
            data={dataGraphs}
            enableGridX={false}
            yScale={{
              type: "linear",
              min: Math.min(yTickValues[0], yMinILS),
              max: Math.max(yTickValues[yTickValues.length - 1], yMaxILS),
            }}
            yFormat={(v: any) => {
              const num = typeof v === "number" ? v : parseFloat(v as string);
              const converted = convertCurr(num);
              return `${currencySymbol}${nFormatter(
                Math.round(Math.abs(converted)),
                2
              )}${converted < 0 ? "-" : ""}`;
            }}
            useMesh={true}
            enableArea={showMonteCarlo}
            areaBaselineValue={0}
            areaOpacity={0.1}
            axisLeft={{
              tickValues: yTickValues,
              tickSize: 0,
              tickPadding: 35,
              tickRotation: 0,
              format: (v: any) => {
                const num = typeof v === "number" ? v : parseFloat(v as string);
                const converted = convertCurr(num);
                return `${currencySymbol}${nFormatter(
                  Math.round(Math.abs(converted)),
                  2
                )}${converted < 0 ? "-" : ""}`;
              },
            }}
            axisBottom={{
              tickValues: tickValues,
              tickSize: 2,
              tickPadding: 8,
              legend: t.chartView.age,
              legendPosition: "middle",
              legendOffset: 35,
            }}
            legends={[]}
            theme={{
              axis: {
                legend: {
                  text: {
                    fontSize: 11,
                  },
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AdvancedRetirementGraph;
